<template>
  <div
    class="control-bar"
    :class="{'is-visible': isBarVisible}"
    data-test="call-control-bar"
    @pointermove="revealBar"
    @pointerenter="revealBar"
    @pointerleave="scheduleHide"
  >
    <iRow
      ref="controlRow"
      :align="isAdaptive ? 'center' : 'between'"
      vertical-align="middle"
      wrap="nowrap"
      gap="small"
      width="fill"
      :data-control-tier="controlTier"
      :style-overrides="{
        paddingTop: 'small',
        paddingBottom: 'small',
        paddingLeft: 'standard',
        paddingRight: 'standard',
      }"
    >
      <iRow
        v-if="!isAdaptive"
        width="fill"
        vertical-align="middle"
        wrap="nowrap"
        :min-width="0"
        overflow="hidden"
        data-test="call-control-leading"
      >
        <slot v-if="controlTier === fullestControlTier" name="leading" />
      </iRow>

      <iControlDock
        ref="dock"
        :open="Boolean(openDrawer)"
        :drawer-key="renderedDrawer || ''"
        data-test="call-control-dock"
        :data-open-drawer="openDrawer || 'none'"
        @pointerenter="cancelHoverClose"
        @pointerleave="scheduleHoverClose"
      >
        <template v-if="renderedDrawer" #drawer>
          <CallDeviceDrawer
            v-if="renderedDrawer === 'microphone' || renderedDrawer === 'camera'"
            ref="deviceDrawer"
            :kind="renderedDrawer"
            picker-test-id-prefix="call-drawer"
            :available-audio-input-devices="availableAudioInputDevices"
            :available-output-devices="availableOutputDevices"
            :available-video-devices="availableVideoDevices"
            :selected-microphone-device-id="selectedMicrophoneDeviceId"
            :selected-output-device-id="selectedOutputDeviceId"
            :selected-camera-device-id="selectedCameraDeviceId"
            @change-microphone="$emit('change-microphone', $event)"
            @change-output="$emit('change-output', $event)"
            @change-camera="$emit('change-camera', $event)"
            @open-settings="openSettingsFromDrawer"
          />
          <iRow
            v-else-if="renderedDrawer === 'reactions'"
            align="left"
            vertical-align="middle"
            wrap="nowrap"
            width="fill"
            data-test="reaction-dock"
          >
            <ReactionQuickBar in-dock :disabled="!isInCall" @send="sendQuickReaction" />
          </iRow>
        </template>

        <iSplitControlButton
          v-if="isControlShown('mic')"
          :label="isMicrophoneEnabled ? 'Turn off microphone' : 'Turn on microphone'"
          menu-label="Audio settings"
          indicator="activity"
          :muted="!isMicrophoneEnabled"
          :expanded="openDrawer === 'microphone'"
          :speaking="isLocalSpeaking"
          :disabled="!isInCall"
          :shortcut="shortcutFor('d')"
          :shortcut-keys="shortcutKeysFor('d')"
          data-test="call-control-mic"
          :data-muted="String(!isMicrophoneEnabled)"
          @click="$emit('toggle-mic')"
          @toggle="toggleDrawer('microphone')"
          @toggle-hover="hoverDrawerTrigger('microphone', $event)"
        >
          <iIcon :name="isMicrophoneEnabled ? 'ic:baseline-mic' : 'ic:baseline-mic-off'" variant="inherit" />
        </iSplitControlButton>

        <iSplitControlButton
          v-if="isControlShown('camera')"
          :label="isCameraEnabled ? 'Turn off camera' : 'Turn on camera'"
          menu-label="Video settings"
          :muted="!isCameraEnabled"
          :expanded="openDrawer === 'camera'"
          :disabled="!isInCall"
          :shortcut="shortcutFor('e')"
          :shortcut-keys="shortcutKeysFor('e')"
          data-test="call-control-camera"
          :data-muted="String(!isCameraEnabled)"
          @click="$emit('toggle-camera')"
          @toggle="toggleDrawer('camera')"
          @toggle-hover="hoverDrawerTrigger('camera', $event)"
        >
          <iIcon :name="isCameraEnabled ? 'ic:baseline-videocam' : 'ic:baseline-videocam-off'" variant="inherit" />
        </iSplitControlButton>

        <CallControlButton
          v-if="isControlShown('screenshare')"
          shape="stadium"
          :active="isScreenShareActive"
          :disabled="!isInCall"
          :label="isScreenShareActive ? 'Stop presenting' : 'Share screen'"
          data-test="call-control-screenshare"
          @click="$emit('toggle-screenshare')"
        >
          <iIcon name="ic:outline-present-to-all" variant="inherit" />
        </CallControlButton>

        <CallControlButton
          v-if="canReact && isControlShown('reactions')"
          shape="stadium"
          :active="openDrawer === 'reactions'"
          :disabled="!isInCall"
          label="Send a reaction"
          data-test="call-control-reactions"
          @click="toggleDrawer('reactions')"
          @pointerenter="hoverDrawerTrigger('reactions', true)"
          @pointerleave="hoverDrawerTrigger('reactions', false)"
        >
          <iIcon name="ic:outline-emoji-emotions" variant="inherit" />
        </CallControlButton>

        <CallControlButton
          v-if="isControlShown('raise-hand')"
          shape="stadium"
          :active="isHandRaised"
          :disabled="!isInCall"
          :label="isHandRaised ? 'Lower hand' : 'Raise hand'"
          data-test="call-control-raise-hand"
          @click="$emit('toggle-hand')"
        >
          <iIcon name="material-symbols:front-hand-outline-rounded" variant="inherit" />
        </CallControlButton>

        <CallControlButton
          v-if="showBreak && isControlShown('break')"
          variant="soft"
          shape="pill"
          :is-toggle="false"
          :disabled="!isInCall"
          label="Take a break: stop your audio and video without ending the stream"
          data-test="call-control-break"
          @click="$emit('break')"
        >
          <iIcon name="ic:baseline-pause-circle-outline" variant="inherit" />
          Take a break
        </CallControlButton>

        <iDropdown
          :show-dropdown="isMoreMenuOpen"
          :z-index="2100"
          overlay-position="top-left"
          surface="bare"
          data-test="call-control-more-menu"
          @close="isMoreMenuOpen = false"
        >
          <template #trigger>
            <CallControlButton
              shape="slim"
              :active="isMoreMenuOpen"
              label="More options"
              data-test="call-control-more"
              @click="toggleMoreMenu"
            >
              <iIcon name="ic:baseline-more-vert" variant="inherit" />
            </CallControlButton>
          </template>
          <template #overlay>
            <iColumn gap="none" wrap="nowrap" :min-width="240">
              <iControlMenu>
                <template v-for="(section, sectionIndex) in moreMenuSections" :key="section.id">
                  <iControlMenuDivider v-if="sectionIndex > 0" />
                  <iColumn
                    gap="none"
                    width="fill"
                    wrap="nowrap"
                    :data-test="`call-control-more-section-${section.id}`"
                  >
                    <iControlMenuItem
                      v-for="item in section.items"
                      :key="item.control"
                      :selected="item.active"
                      :disabled="item.disabled"
                      :data-test="`call-control-more-${item.control}`"
                      @click="chooseMoreMenuItem(item)"
                    >
                      <iIcon
                        :name="item.icon"
                        size="small"
                        variant="inherit"
                        disable-hover
                      />
                      <iText font-size="small" variant="light" :wrap="false">
                        {{ item.label }}
                      </iText>
                    </iControlMenuItem>
                  </iColumn>
                </template>
              </iControlMenu>
            </iColumn>
          </template>
        </iDropdown>

        <CallControlButton
          v-if="canCloseManually && isControlShown('leave')"
          variant="alert"
          shape="pill"
          :is-toggle="false"
          :label="leaveDescription"
          data-test="call-control-leave"
          @click="$emit('leave')"
        >
          <iIcon name="ic:baseline-call-end" variant="inherit" />
          <iText
            v-if="showLeaveLabel"
            variant="light"
            font-size="small"
            :wrap="false"
          >
            {{ leaveLabel }}
          </iText>
        </CallControlButton>
      </iControlDock>

      <iRow
        :align="isAdaptive ? 'center' : 'right'"
        vertical-align="middle"
        wrap="nowrap"
        gap="small"
        :width="isAdaptive ? 'hug' : 'fill'"
        data-test="call-control-trailing"
      >
        <iControlDock
          v-if="isControlShown('roster') || (canShowChat && isControlShown('chat'))"
          density="compact"
          data-test="call-control-side-group"
        >
          <CallControlButton
            v-if="isControlShown('roster')"
            shape="round"
            variant="quiet"
            data-test="call-control-roster"
            :active="isRosterOpen"
            :label="rosterLabel"
            @click="$emit('toggle-roster')"
          >
            <iIcon :name="isRosterOpen ? 'material-symbols:group-rounded' : 'material-symbols:group-outline-rounded'" variant="inherit" />
          </CallControlButton>

          <CallControlButton
            v-if="canShowChat && isControlShown('chat')"
            shape="round"
            variant="quiet"
            :active="isChatOpen"
            :label="isChatOpen ? 'Hide chat' : 'Show chat'"
            data-test="call-control-chat"
            @click="$emit('toggle-chat')"
          >
            <iIcon :name="isChatOpen ? 'material-symbols:chat-rounded' : 'material-symbols:chat-outline-rounded'" variant="inherit" />
          </CallControlButton>
        </iControlDock>
      </iRow>
    </iRow>
  </div>
</template>

<script>
import CallControlButton from "%/apps/video-engine/components/video-call/controls/CallControlButton.vue";
import ReactionQuickBar from "%/apps/video-engine/components/video-call/controls/ReactionQuickBar.vue";
import CallDeviceDrawer from "%/apps/video-engine/components/video-call/controls/CallDeviceDrawer.vue";
import {
  CONTROL_BAR_LAYOUTS,
  CONTROL_BAR_LAYOUT_ADAPTIVE,
  CONTROL_BAR_LAYOUT_STANDARD,
  FULLEST_CONTROL_TIER,
  isCallControlShown,
  nextControlTier,
  widthNeededForControls,
} from "%/apps/video-engine/control-bar-fit";
import { CALL_SHORTCUTS, matchCallShortcut, shortcutKeysLabel, shortcutLabel } from "%/apps/video-engine/call-shortcuts";

const HIDE_AFTER_IDLE_MS = 3500;
const DRAWER_COLLAPSE_MS = 160;
const DRAWERS = ["microphone", "camera", "reactions"];
const HOVER_OPENS_DRAWERS = ["microphone", "camera"];
const HOVER_OPEN_MS = 160;
const HOVER_SWITCH_MS = 110;
const HOVER_CLOSE_MS = 450;
const CLICK_PINS_HOVER_WITHIN_MS = 800;
const MORE_MENU_SECTIONS = ["overflow", "view", "app"];

export default {
  name: "CallControlBar",
  components: { CallControlButton, CallDeviceDrawer, ReactionQuickBar },
  props: {
    isChatOpen: {
      type: Boolean,
      default: false,
    },
    canOpenChat: {
      type: Boolean,
      default: true,
    },
    hasChat: {
      type: Boolean,
      default: true,
    },
    layout: {
      type: String,
      required: false,
      default: CONTROL_BAR_LAYOUT_STANDARD,
      validator: layout => CONTROL_BAR_LAYOUTS.includes(layout),
    },
    keepBarVisible: {
      type: Boolean,
      required: false,
      default: false,
    },
    isInCall: {
      type: Boolean,
      default: false,
    },
    isHandRaised: {
      type: Boolean,
      default: false,
    },
    canReact: {
      type: Boolean,
      default: true,
    },
    isCallInFullscreen: {
      type: Boolean,
      default: false,
    },
    canCloseManually: {
      type: Boolean,
      default: true,
    },
    isInModal: {
      type: Boolean,
      default: false,
    },
    showPopControls: {
      type: Boolean,
      default: true,
    },
    isMicrophoneEnabled: {
      type: Boolean,
      default: false,
    },
    isCameraEnabled: {
      type: Boolean,
      default: false,
    },
    isScreenShareActive: {
      type: Boolean,
      default: false,
    },
    isRosterOpen: {
      type: Boolean,
      default: false,
    },
    isSettingsOpen: {
      type: Boolean,
      default: false,
    },
    showBreak: {
      type: Boolean,
      default: false,
    },
    canAdjustView: {
      type: Boolean,
      default: true,
    },
    rosterLabel: {
      type: String,
      default: "Show participants",
    },
    leaveLabel: {
      type: String,
      default: "Leave",
    },
    leaveDescription: {
      type: String,
      default: "Leave the call",
    },
    showLeaveLabel: {
      type: Boolean,
      default: false,
    },
    isLocalSpeaking: {
      type: Boolean,
      default: false,
    },
    participantCount: {
      type: Number,
      default: 0,
    },
    availableAudioInputDevices: {
      type: Array,
      default: () => [],
    },
    availableOutputDevices: {
      type: Array,
      default: () => [],
    },
    availableVideoDevices: {
      type: Array,
      default: () => [],
    },
    selectedMicrophoneDeviceId: {
      type: String,
      default: null,
    },
    selectedOutputDeviceId: {
      type: String,
      default: null,
    },
    selectedCameraDeviceId: {
      type: String,
      default: null,
    },
  },
  emits: [
    "toggle-mic",
    "toggle-camera",
    "toggle-screenshare",
    "open-adjust-view",
    "toggle-roster",
    "enter-fullscreen",
    "exit-fullscreen",
    "leave",
    "break",
    "pop-out",
    "pop-in",
    "toggle-settings",
    "toggle-chat",
    "toggle-hand",
    "send-reaction",
    "change-microphone",
    "change-output",
    "change-camera",
  ],
  data() {
    return {
      isBarVisible: true,
      isMoreMenuOpen: false,
      openDrawer: null,
      renderedDrawer: null,
      openedByHover: false,
      openedAt: 0,
      hideTimerId: null,
      controlTier: FULLEST_CONTROL_TIER,
      widthNeededByTier: {},
    };
  },
  computed: {
    isAdaptive() {
      return this.layout === CONTROL_BAR_LAYOUT_ADAPTIVE;
    },
    fullestControlTier() {
      return FULLEST_CONTROL_TIER;
    },
    canShowChat() {
      return this.canOpenChat && this.hasChat && !this.isInModal && !this.isCallInFullscreen;
    },
    isReactionBarOpen() {
      return this.openDrawer === "reactions";
    },
    moreMenuItems() {
      const overflow = [
        { control: "screenshare", available: true, disabled: !this.isInCall, active: this.isScreenShareActive, icon: "ic:outline-present-to-all", label: this.isScreenShareActive ? "Stop presenting" : "Share screen", run: () => this.$emit("toggle-screenshare") },
        { control: "reactions", available: this.canReact, disabled: !this.isInCall, active: this.isReactionBarOpen, icon: "ic:outline-emoji-emotions", label: "Send a reaction", run: () => this.toggleDrawer("reactions") },
        { control: "raise-hand", available: true, disabled: !this.isInCall, active: this.isHandRaised, icon: "material-symbols:front-hand-outline-rounded", label: this.isHandRaised ? "Lower hand" : "Raise hand", run: () => this.$emit("toggle-hand") },
        { control: "break", available: this.showBreak, disabled: !this.isInCall, active: false, icon: "ic:baseline-pause-circle-outline", label: "Take a break", run: () => this.$emit("break") },
        { control: "roster", available: true, disabled: false, active: this.isRosterOpen, icon: "ic:baseline-people-outline", label: this.rosterLabel, run: () => this.$emit("toggle-roster") },
        { control: "chat", available: this.canShowChat, disabled: false, active: this.isChatOpen, icon: "ic:outline-chat-bubble-outline", label: this.isChatOpen ? "Hide chat" : "Show chat", run: () => this.$emit("toggle-chat") },
      ].filter(item => item.available && !this.isControlShown(item.control)).map(item => ({ ...item, section: "overflow" }));
      const view = [
        { control: "adjust-view", available: this.canAdjustView, disabled: !this.isInCall, active: false, icon: "material-symbols:dashboard-customize-outline", label: "Adjust view", run: () => this.$emit("open-adjust-view") },
        { control: "fullscreen", available: true, disabled: false, active: this.isCallInFullscreen, icon: this.isCallInFullscreen ? "ic:baseline-fullscreen-exit" : "ic:baseline-fullscreen", label: this.isCallInFullscreen ? "Exit full screen" : "Full screen", run: () => this.$emit(this.isCallInFullscreen ? "exit-fullscreen" : "enter-fullscreen") },
        { control: "pop-in", available: this.isInModal && this.showPopControls, disabled: false, active: false, icon: "carbon:pop-in", label: "Dock the call", run: () => this.$emit("pop-in") },
        { control: "pop-out", available: !this.isInModal && this.showPopControls, disabled: false, active: false, icon: "clarity:pop-out-line", label: "Pop the call out", run: () => this.$emit("pop-out") },
      ].filter(item => item.available).map(item => ({ ...item, section: "view" }));
      const app = [
        { control: "settings", available: true, disabled: false, active: this.isSettingsOpen, icon: "ic:outline-settings", label: "Settings", run: () => this.$emit("toggle-settings") },
      ].filter(item => item.available).map(item => ({ ...item, section: "app" }));

      return [...overflow, ...view, ...app];
    },
    moreMenuSections() {
      return MORE_MENU_SECTIONS
          .map(id => ({ id, items: this.moreMenuItems.filter(item => item.section === id) }))
          .filter(section => section.items.length);
    },
  },
  watch: {
    keepBarVisible(shouldKeep) {
      if (shouldKeep) {
        this.isBarVisible = true;
        this.cancelHide();

        return;
      }
      this.scheduleHide();
    },
    isCallInFullscreen(isFullscreen) {
      if (isFullscreen) {
        this.scheduleHide();

        return;
      }
      this.isBarVisible = true;
      this.cancelHide();
    },
    isInCall(inCall) {
      if (!inCall) {
        this.closeDrawer();
      }
    },
  },
  mounted() {
    this.scheduleHide();
    window.addEventListener("pointermove", this.revealBar, { passive: true });
    window.addEventListener("keydown", this.handleShortcut);
    window.addEventListener("pointerdown", this.closeDrawerOnOutsidePointer, true);
    this.barResizeObserver = new ResizeObserver(this.scheduleFitControlsToWidth);
    this.barResizeObserver.observe(this.$el);
  },
  updated() {
    this.scheduleFitControlsToWidth();
  },
  beforeUnmount() {
    this.cancelHide();
    window.clearTimeout(this.drawerCollapseTimer);
    window.clearTimeout(this.hoverOpenTimer);
    window.clearTimeout(this.hoverCloseTimer);
    window.removeEventListener("pointermove", this.revealBar);
    window.removeEventListener("keydown", this.handleShortcut);
    window.removeEventListener("pointerdown", this.closeDrawerOnOutsidePointer, true);
    this.barResizeObserver?.disconnect();
    window.cancelAnimationFrame(this.fitFrameId);
  },
  methods: {
    isControlShown(control) {
      return isCallControlShown(this.layout, this.controlTier, control);
    },
    shortcutFor(key) {
      return shortcutLabel(key, window.navigator?.platform);
    },
    shortcutKeysFor(key) {
      return shortcutKeysLabel(key, window.navigator?.platform);
    },
    handleShortcut(event) {
      const nothingToDismiss = !this.openDrawer && !this.isMoreMenuOpen;
      if (matchCallShortcut(event) !== CALL_SHORTCUTS.dismiss || nothingToDismiss) {
        return;
      }
      if (this.isMoreMenuOpen) {
        this.isMoreMenuOpen = false;
        return;
      }
      const openPicker = this.drawerPickers().find(picker => picker.isOpen);
      if (openPicker) {
        openPicker.close();
        return;
      }
      this.closeDrawer();
    },
    drawerPickers() {
      return this.$refs.deviceDrawer?.pickers() || [];
    },
    toggleDrawer(drawer) {
      if (!DRAWERS.includes(drawer)) {
        throw new Error(`CallControlBar: unknown drawer ${JSON.stringify(drawer)}; expected one of ${DRAWERS.join(", ")}`);
      }
      window.clearTimeout(this.hoverOpenTimer);
      if (this.openDrawer === drawer) {
        const justOpenedByHover = this.openedByHover && Date.now() - this.openedAt < CLICK_PINS_HOVER_WITHIN_MS;
        if (justOpenedByHover) {
          this.openedByHover = false;
          return;
        }
        this.closeDrawer();
        return;
      }
      this.showDrawer(drawer, { byHover: false });
    },
    showDrawer(drawer, { byHover }) {
      window.clearTimeout(this.drawerCollapseTimer);
      window.clearTimeout(this.hoverCloseTimer);
      this.isMoreMenuOpen = false;
      this.openDrawer = drawer;
      this.renderedDrawer = drawer;
      this.openedByHover = byHover;
      this.openedAt = Date.now();
      this.revealBar();
    },
    hoverDrawerTrigger(drawer, isHovering) {
      if (!DRAWERS.includes(drawer)) {
        throw new Error(`CallControlBar: unknown drawer ${JSON.stringify(drawer)}; expected one of ${DRAWERS.join(", ")}`);
      }
      window.clearTimeout(this.hoverOpenTimer);
      if (!isHovering || !this.isInCall || this.openDrawer === drawer) {
        return;
      }
      if (this.openDrawer) {
        const keepHoverMode = this.openedByHover;
        this.hoverOpenTimer = window.setTimeout(() => this.showDrawer(drawer, { byHover: keepHoverMode }), HOVER_SWITCH_MS);
        return;
      }
      if (HOVER_OPENS_DRAWERS.includes(drawer)) {
        this.hoverOpenTimer = window.setTimeout(() => this.showDrawer(drawer, { byHover: true }), HOVER_OPEN_MS);
      }
    },
    isAnyDrawerMenuOpen() {
      return this.drawerPickers().some(picker => picker.isOpen);
    },
    scheduleHoverClose() {
      window.clearTimeout(this.hoverOpenTimer);
      window.clearTimeout(this.hoverCloseTimer);
      if (!this.openedByHover || !this.openDrawer) {
        return;
      }
      this.hoverCloseTimer = window.setTimeout(() => {
        if (this.openedByHover && !this.isAnyDrawerMenuOpen()) {
          this.closeDrawer();
        }
      }, HOVER_CLOSE_MS);
    },
    cancelHoverClose() {
      window.clearTimeout(this.hoverCloseTimer);
    },
    closeDrawer() {
      window.clearTimeout(this.hoverOpenTimer);
      window.clearTimeout(this.hoverCloseTimer);
      if (!this.openDrawer) {
        return;
      }
      this.openDrawer = null;
      this.openedByHover = false;
      window.clearTimeout(this.drawerCollapseTimer);
      this.drawerCollapseTimer = window.setTimeout(() => {
        if (!this.openDrawer) {
          this.renderedDrawer = null;
        }
      }, DRAWER_COLLAPSE_MS);
    },
    closeDrawerOnOutsidePointer(event) {
      if (!this.openDrawer) {
        return;
      }
      const dock = this.$refs.dock?.$el;
      const target = event.target;
      if (dock?.contains(target) || target?.closest?.(".i-dropdown-content")) {
        return;
      }
      this.closeDrawer();
    },
    openSettingsFromDrawer() {
      this.closeDrawer();
      this.$emit("toggle-settings");
    },
    toggleMoreMenu() {
      this.closeDrawer();
      this.isMoreMenuOpen = !this.isMoreMenuOpen;
    },
    scheduleFitControlsToWidth() {
      window.cancelAnimationFrame(this.fitFrameId);
      this.fitFrameId = window.requestAnimationFrame(this.fitControlsToWidth);
    },
    fitControlsToWidth() {
      const row = this.$refs.controlRow?.$el;
      if (!row) {
        return;
      }
      const next = nextControlTier({
        tier: this.controlTier,
        needed: this.widthControlsNeed(row),
        available: this.$el.clientWidth,
        widthNeededByTier: this.widthNeededByTier,
      });
      if (next.tier === this.controlTier) {
        return;
      }
      this.widthNeededByTier = next.widthNeededByTier;
      this.controlTier = next.tier;
      this.$nextTick(this.scheduleFitControlsToWidth);
    },
    widthControlsNeed(row) {
      const style = window.getComputedStyle(row);
      const dock = this.$refs.dock?.$el;
      const dockStyle = dock ? window.getComputedStyle(dock.querySelector(".control-dock-controls") || dock) : null;
      const dockPadding = dockStyle ? Number.parseFloat(dockStyle.paddingLeft) + Number.parseFloat(dockStyle.paddingRight) : 0;

      return widthNeededForControls({
        buttonWidths: [...row.querySelectorAll(".control-dock-controls button, [data-test=call-control-trailing] button")]
            .map(button => button.getBoundingClientRect().width),
        gap: Number.parseFloat(style.columnGap) || 0,
        padding: Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight) + dockPadding,
      });
    },
    chooseMoreMenuItem(item) {
      if (item.disabled) {
        return;
      }
      this.isMoreMenuOpen = false;
      item.run();
    },
    revealBar() {
      this.isBarVisible = true;
      this.scheduleHide();
    },
    scheduleHide() {
      this.cancelHide();
      if (this.keepBarVisible || this.openDrawer || this.isMoreMenuOpen || !this.isCallInFullscreen) {
        return;
      }
      this.hideTimerId = window.setTimeout(() => {
        this.isBarVisible = false;
      }, HIDE_AFTER_IDLE_MS);
    },
    sendQuickReaction(emoji) {
      this.$emit("send-reaction", emoji);
    },
    cancelHide() {
      if (this.hideTimerId !== null) {
        window.clearTimeout(this.hideTimerId);
        this.hideTimerId = null;
      }
    },
  },
};
</script>

<style scoped>
.control-bar {
  width: 100%;
  z-index: 100;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;
}

@media (prefers-reduced-motion: reduce) {
  .control-bar {
    transition: none;
  }
}

.control-bar.is-visible {
  opacity: 1;
  visibility: visible;
}
</style>
